import { API_PREFIX_CAREER_PATH } from "@/constants/prefix-path-api"
import type { CareerLocalizationRequest, CareerRequest } from "@/types/codegen/data-contracts"
import { message } from "antd"
import { HttpClient } from "../../../../app/api/HttpClient"
import { FilterSearchParams } from "../../../../lib/external-components"

export const fetchCareers = async (searchTerms: Record<string, any> = {}) => {
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "preview",
    filters: {},
    pagination: {
      page: "1",
      pageSize: "10",
    },
    locale: "all",
  }

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]
    if (value === undefined || value === "") return

    if (key === "page" || key === "pageSize") {
      params.pagination = {
        ...params.pagination,
        [key]: String(value),
      }
      return
    }

    if (key === "publicationState") {
      params.publicationState = value === "published" ? "live" : "preview"
      params.filters.publishedAt = value === "draft" ? { $null: true } : undefined
      return
    }

    if (key === "locale") {
      params.locale = typeof value === "string" ? value : undefined
      return
    }

    if (key === "sort") {
      params.sort = value
      return
    }

    if (key === "domain") {
      params.filters.$or = [
        {
          websites: {
            domain: {
              $containsi: value,
            },
          },
        },
        {
          domain: {
            $containsi: value,
          },
        },
        {
          websites: {
            id: {
              $null: true,
            },
          },
        },
        {
          domain: {
            $null: true,
          },
        },
        {
          domain: {
            $eq: "",
          },
        },
      ] as any
      return
    }

    params.filters[key] = { $containsi: value }
  })

  const res = await HttpClient.get(API_PREFIX_CAREER_PATH, {
    params,
    // Career listing uses website/domain policies on BE; skip client auto-injection
    // so records with null/empty website-domain are not excluded unintentionally.
    skipDomainFilter: true,
  })

  return {
    data: res.data,
    meta: res.meta,
  }
}

export const fetchCareerById = async (id: number, locale?: string) => {
  const res = await HttpClient.get(`${API_PREFIX_CAREER_PATH}/${id}`, {
    params: {
      populate: "*",
      locale: locale || "vi",
    },
  })
  return res.data
}

export const createCareer = (data: CareerRequest) => HttpClient.post(API_PREFIX_CAREER_PATH, data)

export const updateCareerById = (id: number, data: CareerRequest) =>
  HttpClient.put(`${API_PREFIX_CAREER_PATH}/${id}`, data)

export const deleteCareerById = (id: number) => HttpClient.delete(`${API_PREFIX_CAREER_PATH}/${id}`)

export const checkCareerSlug = async (slug: string, locale: string) => {
  return HttpClient.get(API_PREFIX_CAREER_PATH, {
    params: {
      filters: { slug: { $eq: slug } },
      publicationState: "preview",
      locale,
    },
  })
}

export const createCareerLocalizationById = async (
  id: number, 
  payload: CareerLocalizationRequest
) => {
  try {
    const res = await HttpClient.post(
      `${API_PREFIX_CAREER_PATH}/${id}/localizations`, payload
    );
    return res.data;
  } catch (err) {
    const error = err as { response?: { data?: { error?: { message?: string } } } };
    message.error(error.response?.data?.error?.message || "Tạo bản dịch lỗi. Vui lòng thử lại sau");
    throw err;
  }
};

export const careers = {
  fetchList: fetchCareers,
  fetchById: fetchCareerById,
  create: createCareer,
  updateById: updateCareerById,
  deleteById: deleteCareerById,
  checkSlug: checkCareerSlug,
  createLocalizationById: createCareerLocalizationById,
}
